import { parseDateToTimestamp, formatDateISO } from "../utils/fifoUtils";
import { formatDateFromTimestamp, formatDateTimeUtcFromTimestamp } from "../utils/dateUtils";
import { rowMatchesTaxYear, formatTaxYearLabel } from "../utils/taxYear.js";
import {
  isUkCountry,
  isUkS104Row,
  getUkMatchingRule,
  formatUkLegMatchingLabel,
} from "../utils/ukTaxUi.js";
import {
  resolveEffectiveCountry,
  getAssignmentTitle,
  shouldUseUkAssignmentLabels,
} from "../utils/taxAssignmentLabels.js";
import { getTokenPriceEUR } from "../utils/priceUtils";
import { useLanguage } from "../contexts/LanguageContext";
import { t, normalizeLang } from "../utils/translations";
import { fmtTokenAmountFixed, fmtEurRow } from "../utils/formatNumbers";
import MaskedEurCell from "./MaskedEurCell";
import {
  backendCurrencyOrFallback,
  blockedTaxLabel,
  currencySymbolOf,
  firstFinite,
  isBlockedTaxRow,
  pickFiatPrice,
  pickFiatTotal,
  pickFiatBuy,
  pickFiatSell,
  pickFiatGrossProfit,
  pickFiatTaxable,
  fiatColumnLabelFromEurKey,
} from "../utils/fiatFields";
export default function FifoDetailRow({ 
  tx, 
  inflows = [], 
  colSpan = 10,
  // 🔎 Liquidity / LP: optional full-precision display (avoid rounding)
  fullPrecisionAmounts = false,
  fullPrecisionDp = 18,
  isEditMode = false,
  nonTaxableInflows = {},
  onNonTaxableChange = () => {},
  selectedCountry = 'DE', // 🎯 NEU: Land für Steuerberechnung
  selectedYear = null,    // 🎯 NEU: Ausgewähltes Steuerjahr für Kontext
  language: propLanguage,  // optional – sonst LanguageContext
  pdfExportMode = false,
  isPreviewMode = false,
  /** Preview: gleiche Maskierung wie die übergeordnete Tabellenzeile (alle Legs + Summary) */
  maskRowEur = false,
}) {
  // 🌐 Sprachverwaltung
  const { language: contextLanguage } = useLanguage();
  const language = normalizeLang(propLanguage ?? contextLanguage ?? "en");
  const showIcons = !pdfExportMode;

  const effectiveCountry = resolveEffectiveCountry(selectedCountry, tx);
  const displayYearLabel = formatTaxYearLabel(selectedYear, effectiveCountry);

  // 💱 Anzeige-Währung: nur wenn Backend currency liefert (nicht nur Land)
  const fiatCurrency = backendCurrencyOrFallback(effectiveCountry, tx);

  // ✅ Helper: Timestamp robust lesen (verhindert 1970-01-01)
  const pickTs = (x) => {
    const raw =
      x?.timestamp ??
      x?.block_timestamp ??
      x?.ts ??
      x?.acquired_ts ??
      x?.acq_ts ??
      x?.created_at ??
      x?.date ??
      0;

    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return n > 1e12 ? Math.floor(n / 1000) : n;
  };
  
  const nftTimestamp = pickTs(tx);
  const oneYearSeconds = 365 * 24 * 60 * 60;
  let remaining = Number(tx.amount) || 0;

  const legQty = (inflow = {}) =>
    Number(inflow.amount ?? inflow.qty ?? inflow.quantity ?? 0) || 0;

  const fifoQtyEps = (baseQty = 0) => {
    const b = Math.abs(Number(baseQty || 0));
    return Math.max(1e-8, b * 1e-10);
  };

  const hasFiatPrice = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
  };

  // 0 ist ein gültiger numerischer EUR-Wert.
  // Nur null/undefined/NaN bedeuten wirklich "kein Wert vorhanden".
  const hasNumericFiatValue = (v) => {
    if (v === null || v === undefined || v === "") return false;
    const n = Number(v);
    return Number.isFinite(n);
  };

  const currencySymbol = currencySymbolOf(fiatCurrency);
  const formatEuro = (value) => `${currencySymbol} ${fmtEurRow(value)}`;
  const pdfMiniWrap = "mt-1 border border-black rounded-none p-[0.7mm]";
  const pdfMiniTitle = "text-[6.7pt] leading-[1.12] font-semibold text-black";
  const pdfMiniText = "text-[6.7pt] leading-[1.12] text-black";
  const pdfMiniValue = "text-[6.7pt] leading-[1.12] font-semibold text-black";
  const pdfHeadCell =
    "px-[0.55mm] py-[0.45mm] border border-black text-[6.7pt] leading-[1.12] font-semibold text-black text-center bg-white";
  const pdfBodyCell =
    "px-[0.55mm] py-[0.45mm] border border-black text-[6.7pt] leading-[1.12] text-black text-center align-middle bg-white";
  const pdfBodyCellUsed =
    "px-[0.55mm] py-[0.45mm] border border-black text-[6.7pt] leading-[1.12] text-black text-center align-middle bg-white";
  const pdfTable = "fifo-detail-table w-full border border-black border-collapse table-auto";
  const pdfTitle = "font-semibold mb-[0.5mm] text-black text-[6.7pt] leading-[1.12]";
  const DUST_QTY_EPS = 1e-8;

  // ✅ Amount formatting
  // Default keeps the UI compact; Liquidity / LP can request full precision to avoid swallowed decimals.
  const formatTokenQty = (value) => {
    if (!fullPrecisionAmounts) return fmtTokenAmountFixed(value, 6);

    const n = Number(value);
    if (!Number.isFinite(n)) return String(value ?? "");

    // Use fixed to avoid scientific notation, then trim trailing zeros.
    const dp = Math.max(0, Math.min(18, Number(fullPrecisionDp) || 18));
    let s = n.toFixed(dp);
    s = s
      .replace(/(\.\d*?[1-9])0+$/g, "$1")
      .replace(/\.0+$/g, "")
      .replace(/^-0$/g, "0");
    return s;
  };

  // 🔧 NEU: Verwende fifoLegs vom Backend mit Haltefrist-Info
  // ✅ Robust: Falls tx.fifoLegs leer ist, verwende inflows (für Liquidity-Tabelle)
  const fifoLegs =
    (Array.isArray(tx.fifoLegs) && tx.fifoLegs.length ? tx.fifoLegs : null) ||
    (Array.isArray(inflows) ? inflows : []) ||
    [];

  const isUK =
    isUkCountry(effectiveCountry) ||
    shouldUseUkAssignmentLabels(effectiveCountry, tx, fifoLegs);
  const txIsUkS104 = isUkS104Row(tx, effectiveCountry) || isUK;
  const assignmentTitle = getAssignmentTitle(
    tx?.token,
    effectiveCountry,
    language,
    tx,
    fifoLegs
  );

  const sortedInflows = txIsUkS104
    ? [...fifoLegs]
    : [...fifoLegs].sort((a, b) => {
        const tA = pickTs(a);
        const tB = pickTs(b);
        return tA - tB;
      });

  const rows = [];

  for (const inflow of sortedInflows) {
    if (remaining <= 0) break;

    const inflowTimestamp = pickTs(inflow);
    const used = Math.min(remaining, legQty(inflow));
    const nftEuroPrice = (pickFiatPrice(tx) ?? tx.euroPrice) || 0;
    const inflowEuroPrice =
      firstFinite(
        inflow.unit_cost_fiat,
        inflow.unitCostFiat,
        inflow.unit_cost_eur,
        inflow.unit_cost,
        inflow.euroPrice
      ) ?? 0;

    const syntheticDustLeg =
      Math.abs(Number(used || 0)) <= DUST_QTY_EPS &&
      Boolean(
        inflow.pool_empty === true ||
        inflow.syntheticCarry === true ||
        inflow.synthetic === true ||
        inflow.syntheticZero === true ||
        inflow.zeroCost === true
      );

    // Backend-Werte auch dann verwenden, wenn sie 0 sind
    const einstand = hasNumericFiatValue(inflow.buyFiat ?? inflow.buyEUR)
      ? Number(inflow.buyFiat ?? inflow.buyEUR)
      : syntheticDustLeg
        ? 0
        : (used * inflowEuroPrice);

    const verkauf = hasNumericFiatValue(inflow.sellFiat ?? inflow.sellEUR)
      ? Number(inflow.sellFiat ?? inflow.sellEUR)
      : syntheticDustLeg
        ? 0
        : (used * nftEuroPrice);

    const gewinn = hasNumericFiatValue(inflow.legProfitFiat ?? inflow.legProfitEUR)
      ? Number(inflow.legProfitFiat ?? inflow.legProfitEUR)
      : (verkauf - einstand);

    // Dust-/Zero-Legs sollen als 0,0000 € erscheinen und nicht als „—“
    const legEinstandMissing = syntheticDustLeg
      ? false
      : Boolean(inflow.costBasisUnknown || inflow.cost_basis_unknown) ||
        (!hasNumericFiatValue(inflow.buyFiat ?? inflow.buyEUR) && !hasFiatPrice(inflowEuroPrice));

    const legVerkaufMissing = syntheticDustLeg
      ? false
      : !hasNumericFiatValue(inflow.sellFiat ?? inflow.sellEUR) && !hasFiatPrice(nftEuroPrice);
    
    const legIsUkS104 = isUkS104Row(inflow, selectedCountry) || txIsUkS104;
    const matchingRule = getUkMatchingRule(inflow) || getUkMatchingRule(tx);
    const matchingRuleLabel = formatUkLegMatchingLabel(
      { ...inflow, costBasisMethod: inflow?.costBasisMethod || tx?.costBasisMethod },
      { ...tx, selectedCountry },
      language
    );

    const rawHeldDays =
      inflow.holdingDays ??
      (inflowTimestamp > 0 && nftTimestamp > 0
        ? Math.floor((nftTimestamp - inflowTimestamp) / (24 * 3600))
        : null);

    const heldDays = legIsUkS104 ? null : rawHeldDays;

    const taxable = legIsUkS104
      ? true
      : inflow.isTaxable !== undefined
        ? inflow.isTaxable
        : (typeof heldDays === "number" && heldDays < 365);

    const inflowKey = `${inflow.sourceHash || inflow.hash || inflow.timestamp || inflow.date}_${used}_${inflow.amount || inflow.qty}`;
    const isManuallyNonTaxable = nonTaxableInflows[inflowKey];

    const inflowAfterNft = legIsUkS104 ? false : inflowTimestamp > nftTimestamp;

    rows.push({
      amount: used,
      date:
        inflowTimestamp > 0
          ? (pdfExportMode
              ? formatDateTimeUtcFromTimestamp(inflowTimestamp)
              : formatDateFromTimestamp(inflowTimestamp))
          : "—",
      heldDays,
      taxable: isManuallyNonTaxable ? false : taxable,
      inflowAfterNft,
      hash: inflow.sourceHash || inflow.hash || "",
      einstand,
      verkauf,
      gewinn: isManuallyNonTaxable ? 0 : gewinn,
      inflowKey,
      isManuallyNonTaxable,
      legEinstandMissing,
      legVerkaufMissing,

      isUkS104: legIsUkS104,
      matchingRule,
      matchingRuleLabel,
      rawHeldDays,
    });

    remaining -= used;
  }

  const totalUsed = rows.reduce((sum, row) => sum + row.amount, 0);
  const remainingDiff = Math.abs(Number(tx.amount) - totalUsed);
  const isPartialMatch = remainingDiff > fifoQtyEps(Number(tx.amount) || totalUsed);
  const hasAnyRow = rows.length > 0;

  const grossProfitValue = hasNumericFiatValue(pickFiatGrossProfit(tx))
    ? Number(pickFiatGrossProfit(tx))
    : rows.reduce((sum, row) => sum + row.gewinn, 0);

  const taxableAmount = hasNumericFiatValue(pickFiatTaxable(tx))
    ? Number(pickFiatTaxable(tx))
    : rows.reduce((sum, row) => sum + (row.taxable ? row.gewinn : 0), 0);

  const grossBuyValue = hasNumericFiatValue(pickFiatBuy(tx))
    ? Number(pickFiatBuy(tx))
    : rows.reduce((sum, row) => sum + row.einstand, 0);

  const grossSellValue = hasNumericFiatValue(pickFiatSell(tx))
    ? Number(pickFiatSell(tx))
    : rows.reduce((sum, row) => sum + row.verkauf, 0);

  const txHasUsableFiatData = Boolean(
    hasNumericFiatValue(pickFiatPrice(tx)) ||
    hasFiatPrice(tx?.euroPrice) ||
    hasNumericFiatValue(pickFiatTotal(tx)) ||
    hasNumericFiatValue(tx?.euroAmount) ||
    hasNumericFiatValue(pickFiatBuy(tx)) ||
    hasNumericFiatValue(pickFiatSell(tx)) ||
    hasNumericFiatValue(pickFiatGrossProfit(tx)) ||
    hasNumericFiatValue(pickFiatTaxable(tx)) ||
    rows.some((row) => !(row.legEinstandMissing || row.legVerkaufMissing))
  );

  const txMarkedMissingPrice = Boolean(
    tx?.pricingMissing ?? tx?.priceMissing ?? tx?.missingFiatPrice
  );

  const ukLegsFiatComplete =
    (isUK || txIsUkS104) &&
    hasAnyRow &&
    rows.every((r) => !r.legEinstandMissing && !r.legVerkaufMissing);

  const taxRowBlocked = isBlockedTaxRow(tx) && !ukLegsFiatComplete;

  const txMissingPrice =
    (txMarkedMissingPrice || !txHasUsableFiatData) && !ukLegsFiatComplete;

  // Ganze Summary nur verstecken, wenn die TX selbst wirklich keine verwertbaren Fiat-Daten hat
  // oder gar keine Legs existieren. Eine einzelne fehlende Dust-Leg darf die Summary nie blockieren.
  const showSummaryPlaceholder = txMissingPrice || !hasAnyRow || taxRowBlocked;

  const previewActive = Boolean(isPreviewMode);
  const maskAllEur = previewActive && maskRowEur;
  const maskSummaryEur = maskAllEur;

  // 🔧 NEU: Handler für Checkbox-Änderungen
  const handleNonTaxableChange = (inflowKey, checked) => {
    onNonTaxableChange(inflowKey, checked);
  };

  return (
    <tr className="fifo-detail-row pdf-tx-detail-row print:bg-white print:align-top print:leading-tight print:border-t-0 print:m-0 print:break-inside-avoid print:page-break-inside-avoid print:break-before-avoid-page print:page-break-before-avoid">
      <td
        colSpan={colSpan}
        className="border-t-0 border-x border-b p-2 print:pt-0 print:pb-2 print:pl-3 print:pr-2 print:border-l-2 print:border-black print:rounded-none print:leading-tight print:align-top print:break-inside-avoid print:page-break-inside-avoid"
      >
      <div className="print:mt-0 print:mb-1 print:leading-tight">
        {/* 🌐 Web: Titel oben */}
<div className={pdfExportMode ? pdfTitle : "font-semibold mb-1 text-gray-700 print:italic print:text-gray-600 print:text-xs print:block"}>
  {assignmentTitle}
  {isPartialMatch && !pdfExportMode && (
    <span
      className="text-yellow-600 ml-2"
      title={`⚠️ ${t('incomplete', language)}`}
    >
      ⚠️
    </span>
  )}
</div>

        {Number(tx?.refundedAmount || 0) > 0 &&
          Number(tx?.grossAmount || 0) > Number(tx?.amount || 0) &&
          !pdfExportMode && (
          <div className="mb-2 text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded border border-slate-200">
            {language === "en"
              ? `Swap refund netted: gross ${formatTokenQty(tx.grossAmount)} − refund ${formatTokenQty(tx.refundedAmount)} = net ${formatTokenQty(tx.amount)} ${tx.token || ""}`
              : `Swap-Refund verrechnet: brutto ${formatTokenQty(tx.grossAmount)} − Refund ${formatTokenQty(tx.refundedAmount)} = netto ${formatTokenQty(tx.amount)} ${tx.token || ""}`}
          </div>
        )}

        {/* 🎯 NEU: Steuerjahr-Kontext Info */}
        {selectedYear && !rowMatchesTaxYear(tx, selectedCountry, Number(selectedYear)) && (
          <div className="mb-2 text-xs text-gray-600 bg-yellow-50 px-2 py-1 rounded border border-yellow-200">
            <div className="flex items-center gap-2">
              {!pdfExportMode && <span className="text-yellow-600">ℹ️</span>}
              <span>
                {language === 'en' 
                  ? `This trade is from ${new Date(tx.timestamp * 1000).getFullYear()} and not relevant for the current tax year ${displayYearLabel}`
                  : `Dieser Trade ist aus ${new Date(tx.timestamp * 1000).getFullYear()} und nicht relevant für das aktuelle Steuerjahr ${displayYearLabel}`
                }
              </span>
            </div>
          </div>
        )}

        {/* 🖨️ Tabelle */}
        <table className={pdfExportMode ? pdfTable : "fifo-detail-table text-xs w-full border border-gray-300 border-collapse print:mt-0 print:mb-0 print:border-t-0 print:border-separate print:border-spacing-0 table-fixed"}>
          {pdfExportMode && (
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
          )}
          <thead>
                         <tr className={pdfExportMode ? "bg-white text-black font-semibold" : "bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 font-semibold"}>
               <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-300 w-[16%] text-center"}>
                 <div className="flex items-center justify-center gap-1">
                   {!pdfExportMode && <span className="text-xs">📊</span>}
                   <span>{t('used', language)}</span>
                 </div>
               </th>
               <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[18%] text-center"}>
                 <div className="flex items-center justify-center gap-1">
                   {!pdfExportMode && <span className="text-xs">📅</span>}
                   <span>
                     {pdfExportMode
                       ? (language === "en" ? "Received on (UTC)" : "Erhalten am (UTC)")
                       : t('receivedOn', language)}
                   </span>
                 </div>
               </th>
               <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[15%] text-center"}>
                 <div className="flex items-center justify-center gap-1">
                   {!pdfExportMode && <span className="text-xs">⏱️</span>}
                   <span>
                     {isUK
                       ? (language === "en" ? "UK matching" : "UK-Matching")
                       : t("holdingDuration", language)}
                   </span>
                 </div>
               </th>
               <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[15%] text-center"}>
                 <div className="flex items-center justify-center gap-1">
                   {!pdfExportMode && <span className="text-xs">💰</span>}
                   <span>{fiatColumnLabelFromEurKey('purchasePriceEUR', fiatCurrency, language, t)}</span>
                 </div>
               </th>
               <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[15%] text-center"}>
                 <div className="flex items-center justify-center gap-1">
                   {!pdfExportMode && <span className="text-xs">💸</span>}
                   <span>{fiatColumnLabelFromEurKey('salePriceEUR', fiatCurrency, language, t)}</span>
                 </div>
               </th>
               <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[15%] text-center"}>
                 <div className="flex items-center justify-center gap-1">
                   {!pdfExportMode && <span className="text-xs">📈</span>}
                   <span>{fiatColumnLabelFromEurKey('profitEUR', fiatCurrency, language, t)}</span>
                 </div>
               </th>
               <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[10%] text-center print-nowrap"}>
                 <div className="flex items-center justify-center gap-1">
                   {!pdfExportMode && <span className="text-xs">⚖️</span>}
                   <span>{t('taxable', language)}</span>
                 </div>
               </th>
               {isEditMode && !previewActive && !pdfExportMode && selectedCountry !== 'GR' && selectedCountry !== 'IT' && !isUK && (
                 <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[10%] text-center"}>
                   <div className="flex items-center justify-center gap-1">
                     {!pdfExportMode && <span className="text-xs">🕐</span>}
                     <span>{t('holdingPeriodOver365Days', language)}</span>
                   </div>
                 </th>
               )}
             </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const maskLegEur = maskAllEur;
              return (
              <tr key={i} className="bg-white hover:bg-blue-50 transition-colors duration-200">
                <td className={pdfExportMode ? pdfBodyCellUsed : "p-2 border border-gray-200 text-center"}>
                  <div className="flex items-center justify-center gap-1">
                    <span className="font-medium text-gray-900">{formatTokenQty(row.amount)}</span>
                    {row.hash && !pdfExportMode && (
                      <span
                        title={`Hash: ${row.hash}`}
                        className="text-blue-400 cursor-help hover:text-blue-600 transition-colors"
                      >
                        🏢
                      </span>
                    )}
                  </div>
                </td>
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center font-medium text-gray-800"}>{row.date}</td>
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center"}>
                  {row.isUkS104 ? (
                    <span
                      className={
                        pdfExportMode
                          ? "text-black"
                          : "px-1.5 py-0.5 bg-sky-100 text-sky-800 rounded-full text-sm font-medium print-nowrap pdf-badge"
                      }
                      title={
                        row.matchingRule === "bed_and_breakfast_30d" && row.rawHeldDays < 0
                          ? (language === "en"
                              ? "UK 30-day matching can use a future acquisition. This is not a FIFO shortage."
                              : "UK-30-Day-Matching darf eine spätere Anschaffung verwenden. Das ist keine FIFO-Shortage.")
                          : undefined
                      }
                    >
                      {row.matchingRuleLabel}
                    </span>
                  ) : typeof row.heldDays === "number" ? (
                    <span className={pdfExportMode ? "text-black" : "px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium print-nowrap pdf-badge"}>
                      {row.heldDays} {t("days", language)}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center font-medium text-gray-800"}>{maskLegEur ? <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} showLock={!pdfExportMode} dense fullWidth /> : (row.legEinstandMissing ? <span className="font-mono text-slate-400">—</span> : formatEuro(row.einstand))}</td>
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center font-medium text-gray-800"}>{maskLegEur ? <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} showLock={!pdfExportMode} dense fullWidth /> : (row.legVerkaufMissing ? <span className="font-mono text-slate-400">—</span> : formatEuro(row.verkauf))}</td>
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center"}>
                  {maskLegEur ? (
                    <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} showLock={!pdfExportMode} dense fullWidth />
                  ) : (row.legEinstandMissing || row.legVerkaufMissing) ? (
                    <span className="font-mono text-slate-400">—</span>
                  ) : pdfExportMode ? (
                    <span className={pdfMiniText}>{formatEuro(row.gewinn)}</span>
                  ) : (
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        row.isManuallyNonTaxable
                          ? "bg-green-100 text-green-800"
                          : row.gewinn > 0
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                      } print-nowrap pdf-badge`}
                    >
                      {formatEuro(row.gewinn)}
                    </span>
                  )}
                </td>
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center"}>
                  {row.isUkS104 ? (
                    <span
                      className={
                        pdfExportMode
                          ? "text-black"
                          : "px-2 py-1 bg-sky-100 text-sky-800 rounded-full text-xs font-semibold print-nowrap pdf-badge"
                      }
                      title={
                        language === "en"
                          ? "Included in UK capital gains calculation before Annual Exempt Amount."
                          : "In UK-Capital-Gains-Berechnung vor Annual Exempt Amount enthalten."
                      }
                    >
                      {language === "en" ? "Included" : "Enthalten"}
                    </span>
                  ) : row.inflowAfterNft ? (
                    <span
                      className={pdfExportMode ? pdfMiniText : "px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold cursor-help print-nowrap pdf-badge"}
                      title={pdfExportMode ? undefined : `⚠️ ${t('timeInconsistent', language)}`}
                    >
                      {pdfExportMode ? (language === "en" ? "Warning" : "Warnung") : "⚠️"}
                    </span>
                  ) : taxRowBlocked ? (
                    pdfExportMode ? (
                      <span className={pdfMiniText}>{language === "en" ? "Review" : "Prüfen"}</span>
                    ) : (
                      <span className="px-2 py-1 bg-amber-100 text-amber-900 rounded-full text-xs font-semibold print-nowrap pdf-badge">
                        {language === "en" ? "Review" : "Prüfen"}
                      </span>
                    )
                  ) : row.taxable ? (
                    pdfExportMode ? (
                      <span className="text-black">{t('yes', language)}</span>
                    ) : (
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold print-nowrap pdf-badge">
                        ✅ {t('yes', language)}
                      </span>
                    )
                  ) : (
                    pdfExportMode ? (
                      <span className="text-black">{t('no', language)}</span>
                    ) : (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold print-nowrap pdf-badge">
                        ❌ {t('no', language)}
                      </span>
                    )
                  )}
                </td>
                {isEditMode && !previewActive && !pdfExportMode && selectedCountry !== 'GR' && selectedCountry !== 'IT' && !isUK && (
                  <td className="p-2 border border-gray-200 text-center">
                    <label className="flex items-center justify-center cursor-pointer hover:bg-blue-50 p-1 rounded transition-colors">
                      <input
                        type="checkbox"
                        checked={row.isManuallyNonTaxable || false}
                        onChange={(e) => handleNonTaxableChange(row.inflowKey, e.target.checked)}
                        className="mr-1 accent-blue-600"
                        title={t('markAsNonTaxable', language)}
                      />
                      <span className="text-xs text-blue-600 font-medium">
                        {row.isManuallyNonTaxable ? "✅" : "🕐"}
                      </span>
                    </label>
                  </td>
                )}
              </tr>
            );
            })}
          </tbody>
        </table>
        
        {/* 💼 KOMPAKTE GEWINN-ZUSAMMENFASSUNG */}
        {rows.length > 0 && (
          <div className={pdfExportMode ? pdfMiniWrap : "mt-3 bg-white border border-gray-200 rounded-md p-3"}>
            <div className="flex items-center justify-between mb-2">
              <span className={pdfExportMode ? pdfMiniTitle : "text-sm font-medium text-gray-700"}>
                {language === 'en' ? 'Profit Analysis' : 'Gewinnanalyse'}
              </span>
              {(() => {
                const rowIsTaxable =
                  rows.some((r) => r.taxable === true) ||
                  fifoLegs.some((l) => l?.isTaxable === true);
                if (taxRowBlocked) {
                  return (
                    <span className={pdfExportMode 
                      ? pdfMiniText
                      : `px-2 py-0.5 rounded-full text-xs font-medium ${
                          'bg-amber-100 text-amber-900'
                        }`}>
                      {blockedTaxLabel(tx, language)}
                    </span>
                  );
                }
                if (isUK || txIsUkS104) {
                  return (
                    <span className={pdfExportMode 
                      ? pdfMiniText
                      : "px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800"}>
                      {language === "en" ? "UK CGT basis" : "UK CGT-Basis"}
                    </span>
                  );
                }
                return (
                  <span className={pdfExportMode 
                    ? pdfMiniText
                    : `px-2 py-0.5 rounded-full text-xs font-medium ${
                        rowIsTaxable ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>
                    {rowIsTaxable 
                      ? (language === 'en' ? 'Taxable' : 'Steuerpflichtig')
                      : (language === 'en' ? 'Tax Free' : 'Steuerfrei')
                    }
                  </span>
                );
              })()}
            </div>
            
            <div className="space-y-1">
              {taxRowBlocked && !pdfExportMode && (
                <div className="text-xs text-amber-800 mb-1">
                  <div>
                    <strong>{language === "en" ? "Gross profit:" : "Bruttogewinn:"}</strong>{" "}
                    {language === "en" ? "Not calculated" : "Nicht berechnet"}
                  </div>
                  <div>
                    <strong>{language === "en" ? "Taxable profit:" : "Steuerpflichtiger Gewinn:"}</strong>{" "}
                    {language === "en" ? "Not calculated" : "Nicht berechnet"}
                  </div>
                  {Number(tx?.fifoShortageQty ?? tx?.missingFifoQty ?? 0) > 0 && (
                    <div className="mt-0.5">
                      {language === "en" ? "Missing FIFO quantity:" : "Fehlende FIFO-Menge:"}{" "}
                      <strong>
                        {formatTokenQty(Number(tx?.fifoShortageQty ?? tx?.missingFifoQty ?? 0))}{" "}
                        {tx?.token || ""}
                      </strong>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className={pdfExportMode ? pdfMiniText : "text-sm text-gray-600"}>
                  {showSummaryPlaceholder
                    ? (language === 'en' ? 'Gross Result' : 'Bruttoergebnis')
                    : grossProfitValue >= 0
                    ? (language === 'en' ? 'Gross Profit' : 'Bruttogewinn')
                    : (language === 'en' ? 'Gross Loss' : 'Bruttoverlust')
                  }
                </span>
                <span className={pdfExportMode ? pdfMiniValue : `text-sm font-semibold ${
                  grossProfitValue >= 0 ? 'text-gray-900' : 'text-red-600'
                }`}>
                  {maskSummaryEur ? <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} showLock={!pdfExportMode} dense /> : showSummaryPlaceholder ? <span className="font-mono text-slate-400">—</span> : formatEuro(grossProfitValue)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                {(() => {
                  return (
                    <>
                      <span className={pdfExportMode ? pdfMiniText : "text-sm text-gray-600"}>
                        {showSummaryPlaceholder
                          ? (language === 'en' ? 'Taxable Result' : 'Steuerliches Ergebnis')
                          : taxableAmount >= 0
                          ? (language === 'en' ? 'Taxable Profit' : 'Steuerpflichtiger Gewinn')
                          : (language === 'en' ? 'Taxable Loss' : 'Steuerpflichtiger Verlust')
                        }
                      </span>
                      <span className={pdfExportMode ? pdfMiniValue : "text-sm font-semibold text-red-600"}>
                        {maskSummaryEur ? <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} showLock={!pdfExportMode} dense /> : showSummaryPlaceholder ? <span className="font-mono text-slate-400">—</span> : formatEuro(taxableAmount)}
                      </span>
                    </>
                  );
                })()}
              </div>
            </div>
            
            <div className={pdfExportMode ? "mt-1 pt-1 border-t border-black" : "mt-2 pt-2 border-t border-gray-200"}>
              <div className={pdfExportMode ? `${pdfMiniText} text-right` : "text-sm text-gray-600 text-right"}>
                {language === 'en' ? 'Cost' : 'Einstand'}: <span className="font-mono">{maskSummaryEur ? <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} showLock={!pdfExportMode} dense /> : showSummaryPlaceholder ? (taxRowBlocked ? <span className="text-amber-800 font-medium">{language === 'en' ? 'Unknown' : 'Unbekannt'}</span> : <span className="text-slate-400">—</span>) : formatEuro(grossBuyValue)}</span> • 
                {language === 'en' ? 'Sale' : 'Verkauf'}: <span className="font-mono">{maskSummaryEur ? <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} showLock={!pdfExportMode} dense /> : showSummaryPlaceholder ? <span className="text-slate-400">—</span> : formatEuro(grossSellValue)}</span>
              </div>
            </div>
          </div>
        )}
        
        {isEditMode && !previewActive && selectedCountry !== 'GR' && selectedCountry !== 'IT' && !isUK && (
          <div className="mt-2 text-xs text-blue-600 italic">
            {t('note', language)}
          </div>
        )}
      </div>
    </td>
  </tr>
);
}